import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { formatCurrency } from '../utils/currency';
import useCurrencyCode from '../hooks/useCurrencyCode';
import useAuth from '../hooks/useAuth';

const STATUS_OPTIONS = ['pending', 'packed', 'paid', 'collected'];

// Renders and manages the staff-facing preorder list, detail view, and creation form.
export default function PreordersPage() {
  const currencyCode = useCurrencyCode();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [preorders, setPreorders] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [pickupTime, setPickupTime] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [opsSummary, setOpsSummary] = useState(null);
  const [opsLoading, setOpsLoading] = useState(false);

  const fetchPreorders = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await api.get('/preorders');
      setPreorders(res.data.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to load pre-orders.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchPreorders();
  }, [fetchPreorders]);

  const fetchOpsSummary = useCallback(async () => {
    if (!isAdmin) return;
    setOpsLoading(true);
    try {
      const res = await api.get('/ops/outbox-summary');
      setOpsSummary(res.data);
    } catch (err) {
      setOpsSummary(null);
      toast.error(err.response?.data?.error || 'Unable to load ops health.');
    } finally {
      setOpsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchOpsSummary();
  }, [fetchOpsSummary]);

  const fetchHistory = useCallback(
    async (preorderId) => {
      if (!preorderId) return;
      setHistoryLoading(true);
      try {
        const res = await api.get(`/preorders/${preorderId}/history`);
        setStatusHistory(res.data.history || []);
      } catch (err) {
        setStatusHistory([]);
        toast.error(err.response?.data?.error || 'Unable to load status history.');
      } finally {
        setHistoryLoading(false);
      }
    },
    []
  );

  const selectPreorder = async (preorderId) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/preorders/${preorderId}`);
      setSelected(res.data);
      setPickupTime('');
      await fetchHistory(preorderId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to load pre-order.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (nextStatus) => {
    if (!selected) return;
    try {
      await api.patch(`/preorders/${selected.id}/status`, { status: nextStatus });
      toast.success('Status updated.');
      const updated = { ...selected, status: nextStatus };
      setSelected(updated);
      setPreorders((prev) =>
        prev.map((p) => (p.id === updated.id ? { ...p, status: nextStatus } : p)),
      );
      await fetchHistory(updated.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status.');
    }
  };

  const triggerNotification = async (event, extraBody = {}) => {
    if (!selected) return;
    setActionLoading((prev) => ({ ...prev, [event]: true }));
    try {
      await api.post(`/preorders/${selected.id}/${event}`, extraBody);
      toast.success('Notification sent.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to trigger notification.');
    } finally {
      setActionLoading((prev) => ({ ...prev, [event]: false }));
    }
  };

  const totalFor = useMemo(
    () => (order) =>
      formatCurrency(
        order.totalAmount ||
          (order.items || []).reduce((sum, item) => sum + Number(item.subtotal || 0), 0),
        currencyCode,
      ),
    [currencyCode],
  );

  const formatDateTime = (value) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleString();
  };

  const formatAge = (value) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return '0m';
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    if (hours === 0) return `${remaining}m`;
    return `${hours}h ${remaining}m`;
  };

  const canSendInvoice = selected?.status === 'pending';
  const canSendReceipt = selected && ['paid', 'collected'].includes(selected.status);
  const canSendPickup = selected && ['packed', 'paid', 'collected'].includes(selected.status);

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-6 overflow-hidden bg-slate-950 px-6 py-6 text-white">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Operations</p>
          <h1 className="text-3xl font-semibold">Pre-Orders</h1>
          <p className="text-sm text-slate-400">
            Create, track, and trigger WhatsApp/email automations via n8n for upcoming collections.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/preorders/new')}
          className="rounded-2xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-slate-900 transition hover:bg-emerald-400"
        >
          New Pre-Order
        </button>
      </header>

      {isAdmin && (
        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-4 text-white shadow-[0_20px_40px_-30px_rgba(8,15,26,0.9)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Ops Health</p>
              <p className="text-sm text-slate-300">Webhook outbox summary (last 24h)</p>
            </div>
            <button
              type="button"
              onClick={fetchOpsSummary}
              className="rounded-full border border-slate-700 px-3 py-1 text-[11px] uppercase tracking-wide text-slate-300 hover:border-emerald-500"
            >
              Refresh
            </button>
          </div>
          {opsLoading && (
            <div className="mt-3 text-xs text-slate-400">Loading ops health…</div>
          )}
          {!opsLoading && opsSummary && (
            <div className="mt-3 grid gap-3 md:grid-cols-[1fr,1fr,1fr,auto]">
              <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Pending</p>
                <p className="text-xl font-semibold text-white">{opsSummary.counts?.pending ?? 0}</p>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Sent</p>
                <p className="text-xl font-semibold text-white">{opsSummary.counts?.sent ?? 0}</p>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Failed</p>
                <p className="text-xl font-semibold text-white">{opsSummary.counts?.failed ?? 0}</p>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-3 text-xs text-slate-300">
                <p className="text-xs uppercase tracking-wide text-slate-500">Oldest pending</p>
                <p>{formatAge(opsSummary.oldestPending?.at)}</p>
                <p className="mt-2 text-xs uppercase tracking-wide text-slate-500">Last failure</p>
                <p>{formatDateTime(opsSummary.lastFailure?.at)}</p>
                <p className="text-[11px] text-slate-500">
                  {opsSummary.lastFailure?.reason || '—'}
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/webhook-outbox')}
                  className="mt-3 inline-flex items-center rounded-full border border-emerald-500 px-3 py-1 text-[11px] uppercase tracking-wide text-emerald-200"
                >
                  Open Outbox
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      <div className="grid flex-1 min-h-0 gap-6 lg:grid-cols-[1.1fr,1.1fr]">
        <section className="flex flex-col min-h-0 overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3 text-xs text-slate-400">
            <span>{preorders.length} pre-order{preorders.length === 1 ? '' : 's'}</span>
            <button
              type="button"
              onClick={fetchPreorders}
              className="rounded-full border border-slate-700 px-3 py-1 text-[11px] uppercase tracking-wide text-slate-300 hover:border-emerald-500"
            >
              Refresh
            </button>
          </div>
          <div
            className="flex-1 min-h-0 overflow-auto pr-1"
            style={{ maxHeight: 'calc(100vh - 12rem)' }}
          >
            <table className="min-w-full divide-y divide-slate-800">
              <thead className="bg-slate-900/50 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 text-sm">
                {loadingList && (
                  <tr>
                    <td colSpan="7" className="px-4 py-4 text-center text-slate-400">
                      Loading pre-orders...
                    </td>
                  </tr>
                )}
                {!loadingList && preorders.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-4 text-center text-slate-400">
                      No pre-orders yet.
                    </td>
                  </tr>
                )}
                {!loadingList &&
                  preorders.map((order) => (
                    <tr key={order.id} className="hover:bg-slate-900/60">
                      <td className="px-4 py-3 text-slate-300">#{order.id}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-white">{order.customerName}</div>
                        <div className="text-xs text-slate-400">{order.customerWhatsApp}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-200">{totalFor(order)}</td>
                      <td className="px-4 py-3">
                        <StatusPill status={order.status} />
                      </td>
                      <td className="px-4 py-3 capitalize text-slate-300">{order.source}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">
                        {new Date(order.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => selectPreorder(order.id)}
                          className="rounded-full border border-emerald-500 px-3 py-1 text-xs font-semibold text-emerald-200 transition hover:bg-emerald-500/10"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)] flex flex-col min-h-0">
          {!selected && !detailLoading && (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              Select a pre-order to view details.
            </div>
          )}
          {detailLoading && (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              Loading pre-order...
            </div>
          )}
          {selected && !detailLoading && (
            <div className="flex h-full min-h-0 flex-col overflow-hidden">
              <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Pre-Order</p>
                  <h2 className="text-2xl font-semibold text-white">
                    #{selected.id} - {selected.customerName}
                  </h2>
                  <p className="text-sm text-slate-400">
                    {selected.customerEmail || 'No email'} - {selected.customerWhatsApp}
                  </p>
                  {selected.customerAddress && (
                    <p className="text-xs text-slate-400">Address: {selected.customerAddress}</p>
                  )}
                </div>
                <StatusPill status={selected.status} />
              </header>

              <div className="flex-1 min-h-0 overflow-auto pr-1 pb-24">
                <div className="mt-3 grid gap-3 rounded-2xl border border-slate-800 bg-slate-950/50 p-4 text-sm text-slate-200">
                  <div className="grid gap-3 lg:grid-cols-[1.1fr,0.9fr]">
                    <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3">
                      <span className="text-slate-400">Status</span>
                      <select
                        value={selected.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white"
                      >
                        {STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm text-slate-200">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Pickup time (optional)</p>
                      <input
                        type="datetime-local"
                        value={pickupTime}
                        onChange={(e) => setPickupTime(e.target.value)}
                        className="mt-2 w-full rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                      />
                      <p className="mt-1 text-xs text-slate-500">Used for receipt/pickup notifications.</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-slate-400">Notes</p>
                    <p className="text-white">{selected.notes || '-'}</p>
                  </div>
                  <div className="divide-y divide-slate-800 rounded-xl border border-slate-800">
                    <div className="flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wide text-slate-400">
                      <span>Items</span>
                      <span>Total {totalFor(selected)}</span>
                    </div>
                    <div className="divide-y divide-slate-800 max-h-72 overflow-auto pr-1">
                      {(selected.items || []).map((item) => (
                        <div key={`${item.productName}-${item.id || item.productId || item.subtotal}`} className="flex items-center justify-between px-4 py-3">
                          <div>
                            <p className="font-semibold text-white">{item.productName}</p>
                            <p className="text-xs text-slate-400">
                              Qty {item.quantity} - {formatCurrency(item.unitPrice, currencyCode)} each
                            </p>
                          </div>
                          <p className="text-sm font-semibold text-white">
                            {formatCurrency(item.subtotal, currencyCode)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-800 bg-slate-950/60">
                    <div className="flex items-center justify-between px-4 py-3 text-xs uppercase tracking-wide text-slate-400">
                      <span>Status history</span>
                      {historyLoading && <span>Loading…</span>}
                    </div>
                    <div className="max-h-56 overflow-auto divide-y divide-slate-800">
                      {historyLoading && (
                        <div className="px-4 py-4 text-xs text-slate-500">
                          Fetching status updates...
                        </div>
                      )}
                      {!historyLoading && statusHistory.length === 0 && (
                        <div className="px-4 py-4 text-xs text-slate-500">
                          No status changes recorded yet.
                        </div>
                      )}
                      {!historyLoading &&
                        statusHistory.map((entry) => (
                          <div key={entry.id} className="px-4 py-3 text-sm text-slate-200">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-semibold text-white">
                                {entry.fromStatus || '—'} → {entry.toStatus}
                              </span>
                              <span className="text-xs text-slate-400">
                                {formatDateTime(entry.createdAt)}
                              </span>
                            </div>
                            <div className="text-xs text-slate-400">
                              By {entry.changedByRole || 'system'}
                              {entry.changedByUserId ? ` #${entry.changedByUserId}` : ''}
                            </div>
                            {entry.note && (
                              <div className="text-xs text-slate-500">Note: {entry.note}</div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 z-10 mt-3 grid shrink-0 gap-3 border-t border-slate-800 bg-slate-900/90 px-3 pt-3 pb-2 backdrop-blur sm:grid-cols-3">
                <ActionButton
                  label="Send Invoice"
                  disabled={!canSendInvoice || actionLoading['send-invoice']}
                  loading={actionLoading['send-invoice']}
                  onClick={() => triggerNotification('send-invoice')}
                  hint="Only when pending"
                />
                <ActionButton
                  label="Send Receipt & Pickup"
                  disabled={!canSendReceipt || actionLoading['send-receipt']}
                  loading={actionLoading['send-receipt']}
                  onClick={() => triggerNotification('send-receipt', { pickupTime: pickupTime || null })}
                  hint="After payment"
                />
                <ActionButton
                  label="Send Pickup Ready"
                  disabled={!canSendPickup || actionLoading['send-pickup']}
                  loading={actionLoading['send-pickup']}
                  onClick={() => triggerNotification('send-pickup', { pickupTime: pickupTime || null })}
                  hint="When packed"
                />
              </div>
            </div>
          )}
        </section>
      </div>

    </div>
  );
}

function StatusPill({ status }) {
  const tone =
    status === 'paid'
      ? 'bg-emerald-500/20 text-emerald-200 border-emerald-600/60'
      : status === 'packed'
      ? 'bg-amber-500/20 text-amber-200 border-amber-600/60'
      : status === 'collected'
      ? 'bg-blue-500/20 text-blue-200 border-blue-600/60'
      : 'bg-slate-800 text-slate-200 border-slate-700';

  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${tone}`}>
      {status}
    </span>
  );
}

function ActionButton({ label, onClick, disabled, loading, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-left transition hover:border-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span className="text-sm font-semibold text-white">{loading ? 'Sending...' : label}</span>
      <span className="text-xs text-slate-400">{hint}</span>
    </button>
  );
}


